import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

/**
 * Health Check — operational dashboard for admins.
 *
 * Five sections matching the recurring health-check audit:
 *   1. Bookings in the last 6 hours (per-location fallback when zero)
 *   2. Square Terminal connection status (live Square Devices API)
 *   3. Square payments processed today, split by location
 *   4. Rebooking-SMS conversions in the last 24h
 *   5. SMS sent in the last 4 hours, grouped by template
 *
 * Manual refresh only — there's a Refresh button in the header.
 */

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
  marginBottom: 'var(--spacing-4)',
};
const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 var(--spacing-3) 0',
  fontSize: '16px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
};
const subtleStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--color-text-muted)',
};
const bigNumberStyle: React.CSSProperties = {
  fontSize: '32px',
  fontWeight: 700,
  lineHeight: 1,
};
const greenBadge: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: 'rgba(34, 197, 94, 0.15)',
  color: '#15803d',
};
const yellowBadge: React.CSSProperties = {
  ...greenBadge,
  backgroundColor: 'rgba(234, 179, 8, 0.15)',
  color: '#a16207',
};
const redBadge: React.CSSProperties = {
  ...greenBadge,
  backgroundColor: 'rgba(220, 38, 38, 0.15)',
  color: '#b91c1c',
};

function StatusBadge({
  ok,
  warn,
  okText = 'OK',
  warnText = 'No activity',
  errText = 'Problem',
}: {
  ok: boolean;
  warn?: boolean;
  okText?: string;
  warnText?: string;
  errText?: string;
}) {
  if (ok) return <span style={greenBadge}>✓ {okText}</span>;
  if (warn) return <span style={yellowBadge}>⚠ {warnText}</span>;
  return <span style={redBadge}>✕ {errText}</span>;
}

function HoursAgo({ hours }: { hours: number | null }) {
  if (hours == null) return <span>—</span>;
  if (hours < 1) return <span>{Math.round(hours * 60)} min ago</span>;
  if (hours < 24) return <span>{hours.toFixed(1)} hours ago</span>;
  return <span>{(hours / 24).toFixed(1)} days ago</span>;
}

export function HealthCheck() {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<any>({
    queryKey: ['health-check'],
    queryFn: api.getHealthCheck,
    staleTime: Infinity, // manual refresh only
    refetchOnWindowFocus: false,
  });

  return (
    <div style={{ padding: 'var(--spacing-4)', maxWidth: 900, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-4)',
          flexWrap: 'wrap',
          gap: 'var(--spacing-2)',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '22px' }}>🩺 Health Check</h1>
          <div style={subtleStyle}>
            {data?.generated_at_bne
              ? `Last fetched: ${data.generated_at_bne} Brisbane`
              : 'Loading…'}
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          style={{
            padding: '8px 16px',
            borderRadius: 'var(--radius-default)',
            border: '1px solid var(--color-border)',
            backgroundColor: isFetching ? 'var(--color-surface)' : 'var(--color-accent)',
            color: isFetching ? 'var(--color-text-muted)' : '#1e1e1e',
            fontWeight: 600,
            cursor: isFetching ? 'wait' : 'pointer',
            minHeight: 40,
          }}
        >
          {isFetching ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {isError && (
        <div style={{ ...cardStyle, borderColor: 'rgba(220, 38, 38, 0.4)' }}>
          <div style={{ color: '#b91c1c', fontWeight: 600 }}>Failed to load health check</div>
          <div style={subtleStyle}>{(error as Error)?.message}</div>
        </div>
      )}

      {isLoading && (
        <div style={cardStyle}>
          <div style={subtleStyle}>Loading…</div>
        </div>
      )}

      {data && (
        <>
          {/* Q1 — Bookings last 6h */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>
              📅 Bookings — last 6 hours{' '}
              <StatusBadge
                ok={data.bookings_last_6h.total > 0}
                warn={data.bookings_last_6h.total === 0}
                okText={`${data.bookings_last_6h.total} booking${data.bookings_last_6h.total === 1 ? '' : 's'}`}
                warnText="No bookings in the last 6h"
              />
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)' }}>
              <div style={bigNumberStyle}>{data.bookings_last_6h.total}</div>
              <div style={subtleStyle}>
                {data.bookings_last_6h.by_source.customer} via customer frontend
                <br />
                {data.bookings_last_6h.by_source.admin} via admin
              </div>
            </div>
            <table
              style={{ width: '100%', marginTop: 'var(--spacing-3)', borderCollapse: 'collapse' }}
            >
              <tbody>
                {data.bookings_last_6h.by_location.map((loc: any) => {
                  const last = data.bookings_last_6h.last_per_location.find(
                    (x: any) => x.location_id === loc.location_id,
                  );
                  return (
                    <tr
                      key={loc.location_id}
                      style={{ borderTop: '1px solid var(--color-border)' }}
                    >
                      <td style={{ padding: '6px 0', fontWeight: 600 }}>{loc.location_name}</td>
                      <td style={{ padding: '6px 0' }}>
                        {loc.count} booking{loc.count === 1 ? '' : 's'} in last 6h
                      </td>
                      <td style={{ padding: '6px 0', ...subtleStyle, textAlign: 'right' }}>
                        {loc.count === 0 && last?.last_booking_at_bne ? (
                          <>
                            last: {last.last_booking_at_bne} (<HoursAgo hours={last.hours_ago} />)
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Q2 — Square Terminals */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>
              💳 Square Terminals{' '}
              <StatusBadge
                ok={data.square_terminals.all_online}
                warn={!data.square_terminals.all_online && data.square_terminals.devices.length > 0}
                errText="All offline"
                okText="All connected"
                warnText="Some offline"
              />
            </h3>
            {data.square_terminals.devices.length === 0 ? (
              <div style={subtleStyle}>No devices reported by Square API.</div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 'var(--spacing-4)' }}>
                {data.square_terminals.devices.map((d: any) => (
                  <li key={d.id} style={{ marginBottom: 4 }}>
                    <strong>{d.name}</strong> —{' '}
                    <span style={d.online ? { color: '#15803d' } : { color: '#b91c1c' }}>
                      {d.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Q3 — Square payments today */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>
              💰 Square payments — today{' '}
              <StatusBadge
                ok={data.square_payments_today.grand_count > 0}
                warn={data.square_payments_today.grand_count === 0}
                okText={`${data.square_payments_today.grand_count} payment${
                  data.square_payments_today.grand_count === 1 ? '' : 's'
                } · $${data.square_payments_today.grand_total_aud.toFixed(2)}`}
                warnText="No payments yet today"
              />
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {data.square_payments_today.by_location.map((loc: any) => {
                  const last = data.square_payments_today.last_per_location.find(
                    (x: any) => x.location_id === loc.location_id,
                  );
                  return (
                    <tr
                      key={loc.location_id}
                      style={{ borderTop: '1px solid var(--color-border)' }}
                    >
                      <td style={{ padding: '6px 0', fontWeight: 600 }}>{loc.location_name}</td>
                      <td style={{ padding: '6px 0' }}>
                        {loc.count} payment{loc.count === 1 ? '' : 's'} · $
                        {loc.total_aud.toFixed(2)}
                      </td>
                      <td style={{ padding: '6px 0', ...subtleStyle, textAlign: 'right' }}>
                        {loc.count === 0 && last?.last_payment_at_bne ? (
                          <>
                            last: {last.last_payment_at_bne} (<HoursAgo hours={last.hours_ago} />)
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Q4 — Rebooking conversions */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>
              🔁 Rebooking conversions — last 24 hours{' '}
              <StatusBadge
                ok={data.rebooking_conversions_24h.total > 0}
                warn={data.rebooking_conversions_24h.total === 0}
                okText={`${data.rebooking_conversions_24h.total} conversion${
                  data.rebooking_conversions_24h.total === 1 ? '' : 's'
                }`}
                warnText="No conversions in last 24h"
              />
            </h3>
            <div style={subtleStyle}>
              Customers who received a Rebooking Reminder and have booked in the last 24h.
            </div>
            <table
              style={{ width: '100%', marginTop: 'var(--spacing-3)', borderCollapse: 'collapse' }}
            >
              <tbody>
                {data.rebooking_conversions_24h.by_location.map((loc: any) => {
                  const last = data.rebooking_conversions_24h.last_per_location.find(
                    (x: any) => x.location_id === loc.location_id,
                  );
                  return (
                    <tr
                      key={loc.location_id}
                      style={{ borderTop: '1px solid var(--color-border)' }}
                    >
                      <td style={{ padding: '6px 0', fontWeight: 600 }}>{loc.location_name}</td>
                      <td style={{ padding: '6px 0' }}>
                        {loc.count} conversion{loc.count === 1 ? '' : 's'}
                      </td>
                      <td style={{ padding: '6px 0', ...subtleStyle, textAlign: 'right' }}>
                        {loc.count === 0 && last?.last_conversion_at_bne ? (
                          <>
                            last: {last.last_conversion_at_bne} (<HoursAgo hours={last.hours_ago} />
                            )
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Q5 — SMS last 4h */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>
              ✉️ SMS sent — last 4 hours{' '}
              <StatusBadge
                ok={data.sms_last_4h.total > 0}
                warn={data.sms_last_4h.total === 0}
                okText={`${data.sms_last_4h.total} SMS`}
                warnText="No SMS in last 4h"
              />
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ ...subtleStyle, textAlign: 'left' }}>
                  <th style={{ padding: '6px 0' }}>Template</th>
                  <th style={{ padding: '6px 0' }}>Sent</th>
                  <th style={{ padding: '6px 0' }}>Failed</th>
                  <th style={{ padding: '6px 0', textAlign: 'right' }}>Last sent</th>
                </tr>
              </thead>
              <tbody>
                {data.sms_last_4h.last_per_template.map((tpl: any) => {
                  const recent = data.sms_last_4h.by_template.find(
                    (x: any) => x.template === tpl.template,
                  );
                  const sent = recent?.sent ?? 0;
                  const failed = recent?.failed ?? 0;
                  return (
                    <tr key={tpl.template} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '6px 0', fontWeight: 600 }}>{tpl.template}</td>
                      <td style={{ padding: '6px 0' }}>{sent}</td>
                      <td
                        style={{
                          padding: '6px 0',
                          color: failed > 0 ? '#b91c1c' : 'var(--color-text-muted)',
                        }}
                      >
                        {failed}
                      </td>
                      <td style={{ padding: '6px 0', ...subtleStyle, textAlign: 'right' }}>
                        {tpl.last_sent_at_bne ? (
                          <>
                            {tpl.last_sent_at_bne} (<HoursAgo hours={tpl.hours_ago} />)
                          </>
                        ) : (
                          <em>never sent</em>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
